import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSlumSoccerFundraisers } from "../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { Loader2 } from "lucide-react";
import { parseFundraiser } from "../utils/fundraiser";
import { formatINR } from "../utils/formatINR";
import { ParticipantProfile } from "../components/fundraiser/ParticipantProfile";
import { FundraiserContent } from "../components/fundraiser/FundraiserContent";
import { DonateButton } from "../components/fundraiser/DonateButton";

const client = generateClient();

export function ParticipantFundraiserPage(props) {
  const { slug, participantSlug } = useParams();
  const [fundraiser, setFundraiser] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchData();
    window.scrollTo(0, 0);
  }, [slug, participantSlug]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setNotFound(false);
      const response = await client.graphql({
        query: getSlumSoccerFundraisers,
        variables: { fundraiserId: slug },
      });

      const data = response.data.getSlumSoccerFundraisers;
      if (!data) {
        setNotFound(true);
        return;
      }

      const parsedFundraiser = parseFundraiser(data);
      const matchedParticipant = (parsedFundraiser.participants || []).find(
        (p) => p.slug === participantSlug
      );

      if (!matchedParticipant) {
        setNotFound(true);
        return;
      }

      setFundraiser(parsedFundraiser);
      setParticipant(matchedParticipant);
    } catch (error) {
      console.error("Error fetching participant:", error);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
        <Loader2 className="animate-spin text-white" size={64} />
      </div>
    );
  }

  if (notFound || !fundraiser || !participant) {
    return (
      <section className="py-24 text-center">
        <h2 className="text-3xl font-bold text-orange-500">
          Participant not found
        </h2>
        <p className="text-gray-600 mt-4">
          The participant you are looking for does not exist.
        </p>
      </section>
    );
  }

  const firstName = participant.name.split(" ")[0];
  const goal = Number(participant.goal) || 0;
  const raised = Number(participant.raised) || 0;
  const percentage = goal > 0 ? Math.round((raised / goal) * 100) : 0;
  const barWidth = Math.min(percentage, 100);

  return (
    <>
      <ParticipantProfile
        name={participant.name}
        profileImage={participant.profileImage}
        intro={participant.intro}
        fundraiserSlug={fundraiser.slug}
        fundraiserTitle={fundraiser.title}
        participantSlug={participant.slug}
      />

      {/* Donation progress */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-orange-50 rounded-xl shadow-lg border border-orange-100 p-6 md:p-8">
            <div className="grid grid-cols-3 gap-4 text-center mb-8">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Raised
                </p>
                <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1">
                  {formatINR(raised)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Progress
                </p>
                <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1">
                  {percentage}%
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Goal
                </p>
                <p className="text-xl md:text-2xl font-bold text-[#FF6600] mt-1">
                  {formatINR(goal)}
                </p>
              </div>
            </div>

            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#efc94c] to-[#FF6600] transition-all duration-700"
                style={{ width: `${barWidth}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      <FundraiserContent html={participant.content} />

      {/* Support CTA */}
      <section className="bg-gray-100 py-14">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-4xl font-bold text-[#FF6600] mb-4">
              Support {firstName}'s Journey
            </h3>
            <p className="text-xl text-gray-700 mb-8">
              Every contribution helps {firstName} get one step closer to their
              goal.
            </p>
            <DonateButton
              label="Donate Now"
              className="px-8 py-3 text-lg"
              fundraiserId={fundraiser.slug}
              fundraiserTitle={fundraiser.title}
              paymentFor="PARTICIPANT"
              participantId={participant.slug}
              participantName={participant.name}
            />
          </div>
        </div>
      </section>
    </>
  );
}
